import { Component, OnDestroy, OnInit, ViewEncapsulation, Input, HostBinding } from '@angular/core';
import { Subscription, of, catchError, map } from 'rxjs';
import { toString as luceneToString } from 'lucene';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';
import { MatRippleModule } from '@angular/material/core';
import { TranslateModule } from '@ngx-translate/core';
import { NgIf } from '@angular/common';

import { pageTransitions } from '../../../../animations/page-transitions';
import { User } from '../../../../models/user';
import { CustomWidget, Widget } from '../../../../models/widget';
import { UserService } from '../../../../services/user.service';
import { QualityIssueFilterService } from '../../../../../claim/services/quality-issue-filter.service';
import { ClaimStatus, CLOSED_STATUS_LIST, PENDING_STATUS_LIST } from '../../../../../claim/models/quality-issue';
import { QualityIssueService } from '../../../../../claim/services/quality-issue.service';
import { LoadingSpinnerComponent } from '../../../utils/loading-spinner.component';

@Component({
	selector: 'copo-issue-widget',
	templateUrl: './issue-follow-up-widget.component.html',
	styleUrl: './issue-follow-up-widget.component.css',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		NgIf,
		TranslateModule,
		MatRippleModule,
		RouterLink,
		MatIconModule,

		MatButtonModule,
		LoadingSpinnerComponent
	]
})
export class IssueFollowUpWidgetComponent implements OnInit, OnDestroy, CustomWidget {
	@HostBinding('class.widget-content') readonly widgetContent = true;
	@Input() widget!: Widget;
	user: User;
	weightTypeLabel = '';
	isClosedLoading = true;
	isPendingLoading = true;
	type?: string;
	pendingCount?: number;
	closedCount?: number;

	closedSub?: Subscription;
	pendingSub?: Subscription;

	constructor(
		private readonly userService: UserService,
		private readonly qualityIssueService: QualityIssueService,
		private readonly qualityIssueFilterService: QualityIssueFilterService
	) {
		this.user = this.userService.user;
	}

	ngOnInit() {
		const contentType = <{ type: string }> this.widget.content;
		this.type = contentType.type;

		this.pendingSub = this.getCount(['pending']).subscribe({
			next: (res: number) => {
				this.pendingCount = res;
				this.isPendingLoading = false;
			},
			error: (error: unknown) => {
				console.error(error);
				this.pendingCount = 0;
				this.isPendingLoading = false;
			}
		});

		this.closedSub = this.getCount(['closed']).subscribe({
			next: res => {
				this.closedCount = res;
				this.isClosedLoading = false;
			},
			error: (error: unknown) => {
				console.error(error);
				this.closedCount = 0;
				this.isClosedLoading = false;
			}
		});
	}

	ngOnDestroy() {
		if (this.pendingSub) {
			this.pendingSub.unsubscribe();
		}
		if (this.closedSub) {
			this.closedSub.unsubscribe();
		}
	}

	getCount(claimStatusList: string[]) {
		return this.user.hasService('QUAPP') ? this.qualityIssueService.getIssuesCount(
			luceneToString(this.qualityIssueFilterService.createDefaultFilters(new Set(claimStatusList)))
		).pipe(
			map(res => res.count),
			catchError((err: unknown) => {
				console.error('error retrieving issues', err);
				return of(0);
			})
		) : of(0);
	}

	getQualityIssueListState(statusStr: 'pending' | 'closed') {
		const filter: (
			{
				id: string;
				values: {
					apiName: string;
					selected: boolean;
					value: string;
				}[][];
			}
		)[] = [
			{
				id: 'status',
				values: [
					[
						ClaimStatus.DRAFT,
						ClaimStatus.PENDING,
						ClaimStatus.CREATED,
						ClaimStatus.CANCELLED,
						ClaimStatus.PAID,
						ClaimStatus.REFUSED,
						ClaimStatus.REGWARNING
					].map(status => ({
						apiName: 'status',
						selected: (statusStr === 'pending' ? PENDING_STATUS_LIST : CLOSED_STATUS_LIST).includes(status),
						value: status
					}))
				]
			}
		];

		return {
			filter
		};
	}
}
